import React, { useMemo, useState } from 'react';
import { TodoTree } from '../../types/todo';
import { getItemPath } from '../../utils/tree';
import { isEligibleForToday } from '../../utils/today';
import { TODAY } from '../../utils/constants';
import { getPriorityColor, getPriorityIcon } from '../../utils/priority';

interface PickStepProps {
  tree: TodoTree;
  selected: string[];
  onToggle: (id: string) => void;
}

interface Candidate {
  id: string;
  name: string;
  /** Ancestor names from the root down to (but excluding) the task itself. */
  path: string;
  priority: string;
}

/**
 * Second wizard step: choose the day's tasks from anywhere in the tree.
 *
 * Candidates are flattened and shown with their ancestry, so picking does not
 * require navigating the hierarchy — the whole point is to see actionable work
 * in one list.
 */
const PickStep: React.FC<PickStepProps> = ({ tree, selected, onToggle }) => {
  const [query, setQuery] = useState('');

  const candidates = useMemo<Candidate[]>(() => {
    return Object.keys(tree)
      .filter(id => isEligibleForToday(tree, id))
      .map(id => {
        const pathIds = getItemPath(tree, id) ?? [];
        // Drop the root container and the task itself from the breadcrumb.
        const ancestors = pathIds.slice(1, -1).map(ancestorId => tree[ancestorId]?.name).filter(Boolean);
        return {
          id,
          name: tree[id].name,
          path: ancestors.join(' › '),
          priority: tree[id].priority,
        };
      });
  }, [tree]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter(
      candidate =>
        candidate.name.toLowerCase().includes(needle) || candidate.path.toLowerCase().includes(needle)
    );
  }, [candidates, query]);

  return (
    <div className="daily-review-step">
      <h4 className="daily-review-step-title">{TODAY.PICK_STEP_TITLE}</h4>

      <input
        type="text"
        className="daily-review-search"
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder={TODAY.PICK_SEARCH_PLACEHOLDER}
        aria-label={TODAY.PICK_SEARCH_PLACEHOLDER}
      />

      {selected.length > TODAY.SOFT_ITEM_LIMIT && (
        <p className="daily-review-warning" role="status">{TODAY.OVER_LIMIT_HINT}</p>
      )}

      {visible.length === 0 ? (
        <p className="daily-review-empty">{TODAY.PICK_EMPTY}</p>
      ) : (
        <ul className="daily-review-candidates">
          {visible.map(candidate => (
            <li key={candidate.id}>
              <label className={`daily-review-candidate ${selected.includes(candidate.id) ? 'is-selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selected.includes(candidate.id)}
                  onChange={() => onToggle(candidate.id)}
                />
                <span
                  className="daily-review-candidate-priority"
                  style={{ color: getPriorityColor(candidate.priority as any) }}
                >
                  {getPriorityIcon(candidate.priority as any)}
                </span>
                <span className="daily-review-candidate-text">
                  <span className="daily-review-candidate-name">{candidate.name}</span>
                  {candidate.path && <span className="daily-review-candidate-path">{candidate.path}</span>}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PickStep;
